import React, { useState } from 'react';
import { InviteLimitBadge } from '../../molecules/InviteLimitBadge';
import './InviteForm.css';

export type InviteType = 'employee' | 'farmer';

export interface InviteLimits {
  /** Remaining employee invites */
  employeeRemaining: number;
  /** Total employee invite limit */
  employeeLimit: number;
  /** Remaining farmer invites for this month */
  farmerRemaining: number;
  /** Date when farmer invites reset (formatted string) */
  farmerResetDate?: string;
}

export interface InviteFormLabels {
  /** Phone input placeholder */
  phonePlaceholder?: string;
  /** Label for employee invite type */
  employeeLabel?: string;
  /** Label for farmer invite type */
  farmerLabel?: string;
  /** Submit button text */
  submitLabel?: string;
  /** Loading state text */
  loadingLabel?: string;
}

export interface InviteFormProps {
  /** Current invite limits */
  limits: InviteLimits;
  /** Handler when invite is submitted */
  onSubmit: (phone: string, type: InviteType) => void;
  /** Whether form is in loading/submitting state */
  loading?: boolean;
  /** Error message to display */
  error?: string;
  /** Optional class name */
  className?: string;
  /** Labels for i18n support */
  labels?: InviteFormLabels;
}

/**
 * Form for inviting new users to the platform.
 * Allows selecting between employee (manage farm) and farmer (own farm) invite types.
 */
const defaultLabels: Required<InviteFormLabels> = {
  phonePlaceholder: 'Phone number (+49 1511 1234567)',
  employeeLabel: 'Can manage my farm',
  farmerLabel: 'Will create their own farm',
  submitLabel: 'Create invite',
  loadingLabel: 'Creating...',
};

export const InviteForm: React.FC<InviteFormProps> = ({
  limits,
  onSubmit,
  loading = false,
  error,
  className = '',
  labels = {},
}) => {
  const [phone, setPhone] = useState('');
  const [inviteType, setInviteType] = useState<InviteType>('employee');

  const l = { ...defaultLabels, ...labels };

  const isEmployeeDisabled = limits.employeeRemaining <= 0;
  const isFarmerDisabled = limits.farmerRemaining <= 0;
  const isCurrentTypeDisabled = inviteType === 'employee' ? isEmployeeDisabled : isFarmerDisabled;
  const isSubmitDisabled = loading || !phone.trim() || isCurrentTypeDisabled;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isSubmitDisabled) {
      onSubmit(phone.trim(), inviteType);
      setPhone('');
    }
  };

  return (
    <div className={`invite-form ${className}`}>
      <form onSubmit={handleSubmit}>
        <div className="invite-form__phone-row">
          <input
            type="tel"
            className="invite-form__phone-input"
            placeholder={l.phonePlaceholder}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            disabled={loading}
            required
          />
        </div>

        <div className="invite-form__type-row">
          <label className={`invite-form__type-option ${isEmployeeDisabled ? 'invite-form__type-option--disabled' : ''}`}>
            <input
              type="radio"
              name="inviteType"
              value="employee"
              checked={inviteType === 'employee'}
              onChange={() => setInviteType('employee')}
              disabled={loading || isEmployeeDisabled}
              className="invite-form__radio"
            />
            <span className="invite-form__type-label">{l.employeeLabel}</span>
            <InviteLimitBadge
              remaining={limits.employeeRemaining}
              type="employee"
              className="invite-form__limit-badge"
            />
          </label>

          <label className={`invite-form__type-option ${isFarmerDisabled ? 'invite-form__type-option--disabled' : ''}`}>
            <input
              type="radio"
              name="inviteType"
              value="farmer"
              checked={inviteType === 'farmer'}
              onChange={() => setInviteType('farmer')}
              disabled={loading || isFarmerDisabled}
              className="invite-form__radio"
            />
            <span className="invite-form__type-label">{l.farmerLabel}</span>
            <InviteLimitBadge
              remaining={limits.farmerRemaining}
              resetDate={limits.farmerResetDate}
              type="farmer"
              className="invite-form__limit-badge"
            />
          </label>
        </div>

        {error && (
          <div className="invite-form__error">
            {error}
          </div>
        )}

        <div className="invite-form__actions">
          <button
            type="submit"
            className="invite-form__submit"
            disabled={isSubmitDisabled}
          >
            {loading ? l.loadingLabel : l.submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InviteForm;
